import { Component, ComponentFactoryResolver, Injector, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Action, ConfirmDialogConfig, CrudConfirmDialogComponent, CrudEditComponent, CrudEditorParentService, CrudEditorSelfService, openCrudRowsEditorDialogInAttributeEditorMode, REPOSITORY_FILE_ROWS_TYPE, UserStorage } from '@scisys-uim/uim-common';
import { ASSET_MAINTENANCE_TYPE, AssetsUser, Firearm, FIREARM_ASSIGNMENT_LOG_TYPE, FIREARM_TYPE, FirearmAssignablePerson, FirearmAssignment, FirearmAssignmentLog } from 'assets-model';
import { FirearmAssignablePersonService, FirearmDocumentPermission, FirearmDocumentService, FirearmService } from 'assets-service';
import { FirearmAcquireDialogComponent, FirearmAcquireDialogConfigData } from '../dialog/firearm-acquire-dialog.component';
import { FirearmSuspendDialogComponent, FirearmSuspendDialogConfigData } from '../dialog/firearm-suspend-dialog.component';
import { FirearmTransferDialogComponent, FirearmTransferDialogConfigData } from '../dialog/firearm-transfer-dialog.component';
import { FirearmItemComponent } from './firearm-item.component';
import { FirearmDisposeDialogComponent, FirearmDisposeDialogConfigData } from '../dialog/firearm-dispose-dialog.component';
import { FirearmDocumentEditDialogComponent, FirearmDocumentEditDialogConfig, FirearmDocumentRowsComponent, FirearmDocumentRowsComponentMode } from './firearm-document-rows.component';
import { FIREARM_POOL_TYPE } from 'assets-model';
import { FirearmReturnDialogComponent, FirearmReturnDialogConfigData } from '../dialog/firearm-return-dialog.component';
import { FirearmAssignmentCreationDialogComponent, FirearmAssignmentCreationDialogConfigData, FirearmAssignmentCreationDialogData } from '../../firearm_assignment/dialog/firearm-assignment-creation-dialog.component';
import { FirearmAssignmentApprovalDialogComponent, FirearmAssignmentApprovalDialogConfigData, FirearmAssignmentApprovalDialogData, FirearmAssignmentApprovalType } from '../../firearm_assignment/dialog/firearm-assignment-approval-dialog.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { openJustfoneDataTransferDialog } from '../../datatransfer/dialog/justfone-data-transfer-dialog.component';


@Component({
  selector: 'app-firearm-editor',
  templateUrl: '../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-edit.component.html',
  styleUrls: ['../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-edit.component.css'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class FirearmEditorComponent extends CrudEditComponent<Firearm, number> implements OnInit {

  poolMemberSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;
  personSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;
  personalFirearmPersonSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;

  get firearmItemComponent(): FirearmItemComponent {
    return <FirearmItemComponent>this.itemComponent;
  }
  get firearmAssignmentLog(): FirearmAssignmentLog {
    return this.firearmItemComponent?.firearmAssignmentLog;
  }
  get pendingActions(): boolean {
    return !!this.firearmAssignmentLog?.pendingActions;
  }

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get approvableFirearmAssignment(): FirearmAssignment {
    return this.firearmAssignmentLog?.assignments?.find(assignment => this.allowsApproval(assignment));
  }


  private firearmDocumentsPermissions: FirearmDocumentPermission[];

  constructor(
    private firearmService: FirearmService,
    private firearmDocumentService: FirearmDocumentService,
    private firearmAssignablePersonService: FirearmAssignablePersonService,
    route: ActivatedRoute,
    router: Router,
    userStorage: UserStorage,
    dialog: MatDialog,
    snackbar: MatSnackBar,
    componentFactoryResolver: ComponentFactoryResolver,
    injector: Injector) {
    super(route, router, userStorage, dialog, snackbar, componentFactoryResolver, injector);

    this.poolMemberSelectionListFilterLoad = (text) => {
      var filters = {
        text: text,
        sourceType: 'RANGER',
        assignmentValid: true,
        firearmPoolId: this.item?.assetId,
      }
      return this.firearmAssignablePersonService.findByFullFilters(filters).pipe(map(data => data.items.filter(
        // A storage manager may also be an eligible ranger with the same email.
        f => this.item.currentAssignmentFirearmAssignmentType != 'ISSUE' || this.item.currentAssignmentEmail != f.email
      )));
    }

    this.personSelectionListFilterLoad = (text) => {
      var filters = {
        text: text,
        sourceType: 'RANGER',
        assignmentValid: true,
        buId: this.item?.asset?.businessUnit?.buId,
        volunteerRanger: false
      }
      return this.firearmAssignablePersonService.findByFullFilters(filters).pipe(map(data => data.items));
    }

    this.personalFirearmPersonSelectionListFilterLoad = (text) => {
      var filters = {
        text: text,
        sourceType: 'RANGER',
        assignmentValid: true,
        buId: this.item?.asset?.businessUnit?.buId,
      }
      return this.firearmAssignablePersonService.findByFullFilters(filters).pipe(map(data => data.items));
    }
  }

  ngOnInit() {
    super.ngOnInit();
  }

  itemSet() {
    super.itemSet();
    const self = this;
    this.actions = [];

    this.editorTitle = this.editorTitle.replace('Firearm Asset', FIREARM_TYPE.typeName(this.item));



    this.actions.push(<Action>{
      title: 'Assignment Log',
      get enabled(): boolean {
        return self.userStorage.currentUserHasAnyAuthority(FIREARM_ASSIGNMENT_LOG_TYPE.editAuthorities) || self.userStorage.currentUserHasAnyAuthority(FIREARM_ASSIGNMENT_LOG_TYPE.viewAuthorities);
      },
      color: true,
      get available(): boolean {
        return self.item?.assetId != null;
      },
      get route() {
        const useEditRoute = self.userStorage.currentUserHasAnyAuthority(FIREARM_ASSIGNMENT_LOG_TYPE.editAuthorities) && !self.readOnly;
        const routeBase = useEditRoute ? FIREARM_ASSIGNMENT_LOG_TYPE.editRouteBase : FIREARM_ASSIGNMENT_LOG_TYPE.viewRouteBase;
        return [...routeBase, self.item?.assetId]
      },
      getQueryParams() {
        return self.backRouteQueryParams();
      }
    });

    this.actions.push(<Action>{
      title: 'View Pending Assignment Actions',
      get enabled(): boolean {
        return self.userStorage.currentUserHasAnyAuthority('READ_FIREARM_ASSIGNMENT');
      },
      color: true,
      get available(): boolean {
        return self.pendingActions && self.item?.assetId != null;
      },
      get route() {
        return ['/firearms'];
      },
      getQueryParams() {
        return { pendingAssignmentAssetId: self.item?.assetId };
      }
    });

    this.actions.push(<Action>{
      title: 'View/Upload Documents',
      get enabled(): boolean {
        return self.isFirearmDocumentsEnabled();
      },
      color: true,
      get available(): boolean {
        return self.isFirearmDocumentsAvailable();
      },
      doAction() {
        self.openFirearmDocumentDialog();
      }
    });

    this.actions.push(<Action>{
      title: 'Submit Request',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['ACQUIRE_FIREARM']) && self.item?.lifecycleState == 'PENDING';
      },
      doAction(...args) {
        self.openFirearmAcquirePopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Approve/Reject',
      get enabled(): boolean {
        return true;
      },
      color: 'primary',
      get available(): boolean {
        return self.approvableFirearmAssignment != null;
      },
      doAction() {
        self.launchApproval(self.approvableFirearmAssignment);
      }
    });

    
    this.actions.push(<Action>{
      title: 'Assign',
      get enabled(): boolean {
        return !self.pendingActions && self.item?.currentAssignmentFirearmAssignmentStorageType != 'TEMPORARY';
      },
      color: true,
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['CREATE_FIREARM_ASSIGNMENT']) && self.item?.lifecycleState == 'IN_SERVICE' 
        && (self.item?.poolFirearm || self.item?.currentAssignmentFirearmAssignmentType == 'STORAGE') && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openFirearmAssignPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'BU/CC Transfer',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['TRANSFER_FIREARM']) && self.item?.lifecycleState == 'IN_SERVICE' && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openTransferFirearmPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Return to Storage',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: "primary",
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['RETURN_FIREARM'])
          && self.item?.lifecycleState == 'IN_SERVICE' && self.item?.currentlyAssigned && self.item?.currentAssignmentFirearmAssignmentType != 'STORAGE' && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openFirearmReturnPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Suspend',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: "accent2",
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['SUSPEND_FIREARM']) && self.item?.lifecycleState == 'IN_SERVICE' && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openFirearmSuspendPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Unsuspend',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: "accent2",
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['SUSPEND_FIREARM']) && self.item?.lifecycleState == 'SUSPENDED' && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openFirearmUnsuspendPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Dispose',
      get enabled(): boolean {
        return !self.pendingActions;
      },
      color: "accent",
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['DISPOSE_FIREARM']) && (self.item?.lifecycleState == 'IN_SERVICE' || self.item?.lifecycleState == 'SUSPENDED') && !self.item?.personalFirearm;
      },
      doAction(...args) {
        self.openFirearmDisposePopup();
      }
    });

    this.actions.push(<Action>{
      title: 'Personal Firearm',
      get enabled(): boolean {
        return !self.pendingActions && !self.unsavedChanges();
      },
      color: "accent2",
      get available(): boolean {
        return !self.createMode && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(['CREATE_FIREARM_ASSIGNMENT']) && self.item?.currentAssignmentFirearmAssignmentType != 'ISSUE';
      },
      doAction(...args) {
        self.openSimpleConfirmationDialog("This will open the assignment dialog for assigning this firearm as a personal firearm. If you wish to assign this normally, please use the 'Assign' button. Do you wish to continue?", "Personal Firearm Assignment", "Continue to Assign Personal Firearm").afterClosed().subscribe(confirm => {
          if (confirm) {
            self.openFirearmAssignPopupForPersonalFirearm();
          }
        });
      }
    });

    this.actions.push(<Action>{
      getTitle(): string { return self.item.firearmPoolExisting ? 'Pool' : 'Create Pool' },
      get enabled(): boolean {
        const hasEditPermission = self.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.editAuthorities);
        const hasViewPermission = self.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.viewAuthorities);
        return hasEditPermission || hasViewPermission;
      },
      getColor(): any {
        return self.item.firearmPoolExisting ? 'primary' : 'accent2';
      },
      get available(): boolean {
        return self.item?.assetId != null && !self.item?.personalFirearm;
      },
      getRoute() {
        var routeBase: any[] = null;
        const hasEditPermission = self.userStorage.currentUserHasAnyAuthority(FIREARM_POOL_TYPE.editAuthorities);
        const useEditRoute = hasEditPermission && !self.readOnly;

        if (!self.item.firearmPoolExisting) {
          routeBase = FIREARM_POOL_TYPE.createRouteBase;
        } else {
          routeBase = useEditRoute ? FIREARM_POOL_TYPE.editRouteBase : FIREARM_POOL_TYPE.viewRouteBase;
        }
        return [...routeBase, self.item?.assetId];
      },
      getQueryParams() {
        return self.backRouteQueryParams();
      }
    });
    this.actions.push(<Action>{
      title: 'Maintenance',
      get enabled(): boolean {
        return true;
      },
      color: true,
      get available(): boolean {
        return self.item?.assetId != null && !self.readOnly && self.userStorage.currentUserHasAnyAuthority(ASSET_MAINTENANCE_TYPE.editAuthorities);
      },
      get route() {
        return [...ASSET_MAINTENANCE_TYPE.searchRouteBase, self.item?.assetId, self.item?.asset?.composedAssetClass]
      }
    });

    this.actions.push(<Action>{
      title: 'Audit',
      get enabled(): boolean {
        return true;
      },
      color: true,
      get available(): boolean {
        return self.item?.assetId != null;
      },
      get route() {
        return [...FIREARM_TYPE.auditRouteBase, self.item?.assetId]
      },
      getQueryParams() {
        return self.backRouteQueryParams();
      }
    });

    this.actions.push(<Action>{
      title: 'Justfone Debug',
      get enabled(): boolean { return !self.unsavedChanges() },
      color: "primary",
      get available(): boolean {
        return self.currentUserHasAnyAuthority("IS_SUPERUSER");
      },
      doAction() {
        openJustfoneDataTransferDialog(self.dialog, 'Firearm', self.item.assetId);
      }
    });
    if (this.item?.assetId != null) {
      this.firearmDocumentService.getFirearmPermissions(this.item.assetId, null).subscribe(permissions => this.firearmDocumentsPermissions = <FirearmDocumentPermission[]>permissions);
    }
  }

  allowsApproval(element: FirearmAssignment): boolean {
    if (element.nextAction == 'BU_APPROVAL') {
      if (this.eAssetsUser?.businessUnit?.buId == element?.businessUnit?.buId) {
        return this.userStorage?.currentUserHasAnyAuthority('BU_FIREARM_ASSIGNMENT_APPROVER');
      }
    }
    if (element.nextAction == 'TRANSFER_BU_APPROVAL') {
      if (this.eAssetsUser?.businessUnit?.buId == element?.transferAsset?.businessUnit?.buId
        && this.eAssetsUser.isSameUser(element?.user?.user)) {
        return this.userStorage?.currentUserHasAnyAuthority('BU_FIREARM_ASSIGNMENT_APPROVER');
      }
    }
    if (element.nextAction == 'LEAD_APPROVAL') {
      if (this.userDetails?.username == element?.buApprovedRejectedBy || this.userDetails?.username == element?.transferAsset?.approvedRejectedBy) {
        return false;
      }
      return this.userStorage?.currentUserHasAnyAuthority('LEAD_FIREARM_ASSIGNMENT_APPROVER');
    }
    return false;
  }

  launchApproval(item: FirearmAssignment) {
    if (item == null) {
      return;
    }
    let approvalType: FirearmAssignmentApprovalType;
    if (item.nextAction == 'BU_APPROVAL') {
      approvalType = 'BU';
    }
    if (item.nextAction == 'TRANSFER_BU_APPROVAL') {
      approvalType = 'TRANSFER_BU';
    }
    if (item.nextAction == 'LEAD_APPROVAL') {
      approvalType = 'LEAD';
    }
    const ref: MatDialogRef<FirearmAssignmentApprovalDialogComponent, FirearmAssignmentApprovalDialogData> = this.dialog.open(FirearmAssignmentApprovalDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentApprovalDialogConfigData>{
        firearmAssignment: item,
        approvalType: approvalType
      }
    });
    ref.afterClosed().subscribe(result => {
      if (result != null) {
        this.service.get(this.item.assetId).subscribe(firearm => {
          this.postSave(firearm);
          this.itemComponent.setDefaultDisabledFields();
        });
      }
    });
  }


  openFirearmAssignPopup() {
    const self = this;
    var ref: MatDialogRef<FirearmAssignmentCreationDialogComponent, FirearmAssignmentCreationDialogData> = this.dialog.open(FirearmAssignmentCreationDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentCreationDialogConfigData>{
        firearmAssignmentType: 'ISSUE',
        firearm: this.item,
        personSelectionListFilterLoad: this.item?.poolFirearm ? this.poolMemberSelectionListFilterLoad : this.personSelectionListFilterLoad
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Assignment Initiated');
        }
      }
    );
  }

  openFirearmAssignPopupForPersonalFirearm() {
    const self = this;
    var ref: MatDialogRef<FirearmAssignmentCreationDialogComponent, FirearmAssignmentCreationDialogData> = this.dialog.open(FirearmAssignmentCreationDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAssignmentCreationDialogConfigData>{
        firearmAssignmentType: 'ISSUE',
        firearm: this.item,
        personalFirearmAssignment: true,
        personSelectionListFilterLoad: this.personalFirearmPersonSelectionListFilterLoad
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Assignment Initiated');
        }
      }
    );
  }

  

  openFirearmAcquirePopup() {
    var ref: MatDialogRef<FirearmAcquireDialogComponent, FirearmAcquireDialogConfigData> = this.dialog.open(FirearmAcquireDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmAcquireDialogConfigData>{
        firearm: this.item
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Acquisition Initiated');
          this.service.get(result.firearm.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        }
      }
    );
  }

  openFirearmDisposePopup() {
    var ref: MatDialogRef<FirearmDisposeDialogComponent, FirearmDisposeDialogConfigData> = this.dialog.open(FirearmDisposeDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmDisposeDialogConfigData>{
        firearm: this.item
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Disposal Initiated');
          this.service.get(result.firearm.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        }
      }
    );
  }


  openFirearmReturnPopup() {
    var ref: MatDialogRef<FirearmReturnDialogComponent, FirearmReturnDialogConfigData> = this.dialog.open(FirearmReturnDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmReturnDialogConfigData>{
        firearm: this.item,
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Returned');
          this.service.get(result.firearm.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        }
      }
    );
  }

  openFirearmSuspendPopup() {
    var ref: MatDialogRef<FirearmSuspendDialogComponent, FirearmSuspendDialogConfigData> = this.dialog.open(FirearmSuspendDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmSuspendDialogConfigData>{
        firearm: this.item,
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Suspended');
          this.service.get(result.firearm.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        }
      }
    );
  }

  openFirearmUnsuspendPopup() {
    const self = this;
    var dialogRef: MatDialogRef<CrudConfirmDialogComponent, boolean> = this.openConfirmationDialog(this.item, {
      data: <ConfirmDialogConfig<Firearm, number>>{
        title: 'Unsuspend Firearm?',
        action: 'Confirm',
      }
    });
    dialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.firearmService.unsuspendFirearm({
          assetId: self.item.assetId
        }).subscribe(result => {
          this.openSnackbar('Firearm Unsuspended');
          this.service.get(result.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        });
      }
    });
  }

  openTransferFirearmPopup() {
    var ref: MatDialogRef<FirearmTransferDialogComponent, FirearmTransferDialogConfigData> = this.dialog.open(FirearmTransferDialogComponent, {
      panelClass: 'dialog-resizable',
      height: '600px',
      width: '900px',
      data: <FirearmTransferDialogConfigData>{
        firearm: this.item,
      }
    })
    ref.afterClosed().subscribe(
      result => {
        if (result != null) {
          this.openSnackbar('Firearm Transfer Initiated');
          this.service.get(result.firearm.assetId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        }
      }
    );
  }


  openFirearmDocumentDialog() {
    var showActions = [];
    var modesAvailable: FirearmDocumentRowsComponentMode[] = [];
    if (this.userDetails.hasAnyAuthority('READ_FIREARM_DOCUMENT')) {
      showActions.push('download');
      modesAvailable.push('LIST_DOCUMENTS');
    }
    if (this.userDetails.hasAnyAuthority('UPLOAD_FIREARM_DOCUMENT')) {
      showActions.push('delete');
      modesAvailable.push('UPLOAD_DOCUMENTS');
    }
    var title = 'Firearm Asset Documents';
    if (this.item) {
      title = title.replace('Firearm Asset', FIREARM_TYPE.typeName(this.item));
    }
    const dialogConfig: FirearmDocumentEditDialogConfig<number> = {
      dialogTitle: title,
      showActions: showActions,
      modesAvailable: modesAvailable,
      permissions: this.firearmDocumentsPermissions,
      firearm: this.item,
      allowSelectUploadDocType: true,
      config: {
        rowsItemComponentType: FirearmDocumentRowsComponent,
        rowsType: REPOSITORY_FILE_ROWS_TYPE,
        readOnly: false
      },
      supportsAdd: false,
      supportsSave: false,
      rows: [],
    };

    const dialogRef = openCrudRowsEditorDialogInAttributeEditorMode(this.dialog, {
      panelClass: ['contract-document-dialog', 'dialog-resizable'],
      height: '80vh',
      minWidth: '60vw',
      //minHeight: '70vh',
      data: dialogConfig
    }, FirearmDocumentEditDialogComponent);
  }

  isFirearmDocumentsAvailable() {
    return this.userDetails.hasAnyAuthority('READ_FIREARM_DOCUMENT');
  }

  isFirearmDocumentsEnabled() {
    return this.firearmDocumentsPermissions?.length > 0;
  }
}
